import React, { useEffect } from "react";
import { RCCAlert, RCCButton, RCCGrid, RCCHidden, RCCIcon, RCCInput, RCCRangeSlider, RCCTooltip } from "react-common-components";
import { useFieldArray } from "react-hook-form";
import SortableContainer from "src/components/Sortable/Container";
import SortableHandle from "src/components/Sortable/Handle";
import SortableItem from "src/components/Sortable/Item";
import Title from "src/components/Title";
import { useTranslation } from "src/context/Translations/context";
import { v4 } from "uuid";
import { restrictToVerticalAxis } from "../Sortable/Container/modifiers";
import { IComponentChecklists } from "./interface";
import ChecklistsItem from "./item";
import cardStyle from "./styles";

const Checklists: React.FC<IComponentChecklists> = ({
	subTitle,
	error,
	checklistsDefaultValue,
	disabled = false,
	register,
	unregister,
	clearErrors,
	control,
	names: {
		nameOfID,
		nameOfItemDesc,
		nameOfItemID,
		nameOfItemVerified,
		nameOfItems,
		nameOfTitle,
		nameOfObject,
		nameOfRangeSlider,
		nameOfItemRangeSlider
	}
}) => {
	const { translate } = useTranslation();

	const { 
		fields: fieldsChecklists, 
		remove: removeChecklists,
		append: appendChecklists,
		move: moveChecklists
	} = useFieldArray({
		control,
		name: nameOfObject,
		shouldUnregister: true
	});

	const addChecklist = () => {
		clearErrors(nameOfObject);
		const checklist: any = {};
		checklist.id = disabled ? "disabled" : v4();
		checklist.key = checklist.id;
		checklist[nameOfID] = "0";
		checklist[nameOfTitle] = "";
		checklist[nameOfRangeSlider] = 1;

		const checklistItem: any = {};
		checklistItem.id = disabled ? "disabled" : v4();
		checklistItem.key = checklistItem.id;
		checklistItem[nameOfItemID] = "0";
		checklistItem[nameOfItemDesc] = "";
		checklistItem[nameOfItemVerified] = "N";
		checklistItem[nameOfItemRangeSlider] = 1;

		checklist[nameOfItems] = [checklistItem];

		appendChecklists(checklist);
	};

	const removeChecklist = (index: number) => {			
		removeChecklists(index);
	};

	const handleDragEnd = (activeId: string, overId: string) => {
		const activeIndex = fieldsChecklists.findIndex((checks) => checks.id === activeId);
		const overIndex = fieldsChecklists.findIndex((checks) => checks.id === overId);
		moveChecklists(activeIndex, overIndex);
		unregister(nameOfObject);
	};

	useEffect(() => {
		appendChecklists(checklistsDefaultValue.map((checklists) => ({
			...checklists,
			id: disabled ? "disabled" : v4()
		})));
	}, []);


	return (
		<RCCGrid
			container
			item
			direction="column"
			alignItems="center"
			justifyContent="center"
		>
			{
				subTitle && 
				<RCCGrid container item justifyContent="flex-start">
					<Title color="grey" size="xs"> {subTitle}. </Title>
				</RCCGrid>
			}
			
			<RCCGrid
				container
				item
			>
				<RCCGrid
					container
					item
					justifyContent="flex-end"
				>
					<RCCTooltip
						color="purple"
						text={translate("Adicionar Checklist")}
					>
						<RCCButton
							color="purple"
							icon={{
								name: "plus"
							}}
							layout="isRounded"
							size="md"
							onClick={addChecklist}
							disabled={disabled}
						/>
					</RCCTooltip>
				</RCCGrid>
			</RCCGrid>
								
			<RCCGrid
				container
				item
			>
				{error[nameOfObject] && error[nameOfObject].message && (
					<Title color="red" size="xs">
						<RCCIcon
							name="infoCircle"
							color="var(--red)"
							style={{ marginRight: "8px"}}
						/>
						{error[nameOfObject].message}
					</Title>
				)}
				{fieldsChecklists.length === 0 && (
					<RCCAlert
						color="grey"
						title={translate("Nenhum Checklist adicionado")}
						description={translate("Aperte no botão acima para adicionar")}
					/>
				)}
				<SortableContainer 
					items={fieldsChecklists}
					onDragEnd={handleDragEnd}
					modifiers={[restrictToVerticalAxis]}
				>	
					{fieldsChecklists.map((checks: any, index) => (
						<SortableItem id={checks.id} key={checks.key ? checks.key : `checklists-${checks[nameOfID]}`}>
							<RCCGrid
								container
								item
								direction="column"
								styleInline={cardStyle}
							>
								<RCCGrid container item>
									<RCCGrid container item sm={6} xs={12}>
										<Title color="purple" size="md">
											{index + 1}. {translate("Checklist")}
											{error[nameOfObject] && error?.[nameOfObject][index]?.[nameOfItems]?.message && (
												<span style={{ display: "flex", alignItems: "center", color: "var(--red)", fontSize: "0.8rem", marginLeft: "4px"}}>
													<RCCIcon
														name="infoCircle"
														color="var(--red)"
														style={{ marginRight: "8px"}}
													/>
													{error[nameOfObject][index]?.[nameOfItems]?.message}
												</span>
											)}
										</Title>
									</RCCGrid>
									<RCCHidden only="xs">
										<RCCGrid container item sm={6}>
											<Title color="purple" size="sm">
												{translate("Peso")}
											</Title>
										</RCCGrid>
									</RCCHidden>
								</RCCGrid>
							
								<RCCGrid
									container
									item
									alignItems="flex-end"
									spacing={1}
								>
									<input 
										type="text"
										value={checks[nameOfID]}
										{...register(`${nameOfObject}[${index}].${nameOfID}`)} 
										style={{ display: "none" }} 
										readOnly
									/>
									<RCCGrid
										container
										item
										justifyContent="center"
										xs={12}
										sm={6}
									>
										<RCCInput 
											register={register}
											name={`${nameOfObject}[${index}].${nameOfTitle}`}
											label=""
											maxLength={40}
											defaultValue={checks[nameOfTitle]}
											error={error[nameOfObject] && error?.[nameOfObject][index]?.[nameOfTitle]}
											disabled={disabled}
											styleInline="height: 34px;"
										/>
									</RCCGrid>
									<RCCGrid
										container
										item
										justifyContent="flex-end"
										xs={12}
										sm={3}
									>
										<RCCRangeSlider
											control={control}
											name={`${nameOfObject}[${index}].${nameOfRangeSlider}`}
											color="purple"
											label=""
											defaultValue={checks[nameOfRangeSlider]}
											min={1}
											max={100}
											disabled={disabled}
											valueLabelDisplay="on"
											styleInline=".MuiSlider-valueLabel > span > span{ color: var(--input-font-color); }"
										/>
									</RCCGrid>
									<RCCGrid
										container
										item
										justifyContent="flex-end"
										xs={12}
										sm={3}
									>
										<SortableHandle>
											<RCCTooltip
												color="grey"
												text={translate("Ordenar")}
											>
												<RCCButton
													color="transparent"
													icon={{
														name: "arrows",
														color: "var(--grey)"
													}}
													layout="isRounded"
													disabled={disabled}
													tabIndex={-1}
												/>
											</RCCTooltip>	
										</SortableHandle>

										<RCCTooltip
											color="red"
											text={translate("Excluir")}
										>
											<RCCButton
												color="transparent"
												icon={{
													name: "trash",
													color: "var(--red)"
												}}
												layout="isRounded"
												onClick={() => removeChecklist(index)}
												disabled={disabled}
												tabIndex={-1}
											/>
										</RCCTooltip>
									</RCCGrid>
								</RCCGrid>
							
								<ChecklistsItem
									disabled={disabled}
									error={error[nameOfObject] ? error?.[nameOfObject][index] || {} : {}}
									index={index}
									items={checks[nameOfItems]}
									register={register}
									control={control}
									names={{
										nameOfItemDesc,
										nameOfItemID,
										nameOfItemVerified,
										nameOfItems,
										nameOfObject,
										nameOfItemRangeSlider
									}}
								/>

							</RCCGrid>											
						</SortableItem>
					))}
				</SortableContainer>
			</RCCGrid>
		</RCCGrid>
	);
};

export default Checklists;
